import React, { useState, useEffect } from 'react';
import { 
  Drawer, Box, Typography, TextField, Button, Stack,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, ListSubheader 
} from '@mui/material';
import toast from 'react-hot-toast';
import { fget } from '../../axios/axiosCalls';
import { formatDateToYYYYMMDD } from '../../utils/utils';

const BookingEditDrawer = ({ open, onClose, booking, onSave }) => {
  const [formData, setFormData] = useState({
    propertyId: '',
    propertyName: '', // Add propertyName to state
    bedSpaceId: '',
    startDate: '',
    endDate: ''
  });

  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPropertyDetails = async (id) => {
    try {
      const response = await fget({ url: `/api/v2/properties/${id}` });
      if (response.data.status === 'Success') {
        const propertyData = response.data.property;
        setRooms(propertyData.rooms || []);
      }
    } catch (error) {
      toast.error('Error fetching property details');
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await fget({ url: '/api/v2/properties' });
        if (response.data.status === 'Success') {
          setProperties(response.data.data);
        }
      } catch (error) {
        toast.error('Error fetching properties');
      }
    };
    fetchProperties();
  }, []);

  useEffect(() => {
    if (booking) {
      
      setFormData({
        propertyId: booking.propertyId || '',
        propertyName: booking.property || '', // Set property name
        bedSpaceId: booking.bedSpaceId || '',
        startDate: booking.startDate ? formatDateToYYYYMMDD(booking.startDate) : '',
        endDate: booking.endDate ? formatDateToYYYYMMDD(booking.endDate) : ''
      });
      
      // Fetch property details if propertyId exists
      if (booking.propertyId) {
        fetchPropertyDetails(booking.propertyId);
      }
    }
  }, [booking]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  // Transform rooms data
  const transformedRooms = rooms.map(room => ({
    roomTitle: room.roomTitle,
    description: room.description,
    beds: room.beds.list.map(bed => ({
      id: bed.id,
      label: `${bed.bedSpace} - £${bed.bedRate}/${bed.bedRatePer}`,
      disabled: bed.isAllotted && bed.id !== formData.bedSpaceId
    }))
  }));

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 3 }}>
        <Typography variant="h6" gutterBottom>Edit Booking</Typography>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Property"
              value={formData.propertyName}
              disabled
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Bed Space</InputLabel>
              <Select
                value={formData.bedSpaceId}
                onChange={(e) => setFormData({...formData, bedSpaceId: e.target.value})}
                label="Bed Space"
                disabled={!formData.propertyId}
              >
                {transformedRooms.map((room) => [
                  <ListSubheader key={room.roomTitle}>
                    {room.description}
                  </ListSubheader>,
                  ...room.beds.map((bed) => (
                    <MenuItem 
                      key={bed.id}
                      value={bed.id}
                      disabled={bed.disabled} 
                      sx={{ pl: 4 }}
                    >
                      {bed.label}
                    </MenuItem>
                  ))
                ])}
              </Select>
            </FormControl>

            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({...formData, endDate: e.target.value})}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

export default BookingEditDrawer;